import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector, RootStateOrAny } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { SubmitHandler, FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import {
  ListCarriersActions,
  ListCarriersState,
} from 'store/ducks/settings/carriers';
import { useValidation, useTranslation } from 'hooks';
import { translations } from './translations';

import * as S from './styles';
import { MainContainer, Modal, Alert } from 'components/shared';
import { Input, Select, InputMask } from 'components/shared/Form';

import {
  DeleteCarrierAreaContactActions,
  DeleteCarrierAreaContactState,
  FetchCarrieAreaContactState,
  FetchCarrierAreaContactActions,
  UpdateCarrierAreaContactActions,
  UpdateCarrierAreaContactState,
} from 'store/ducks/settings/carrier-area-contact';

interface IParams {
  id: string;
}

export const EditCarrierContact: React.FC = () => {
  const { id } = useParams<IParams>();
  const formRef = useRef<FormHandles>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const { handleFormErrors } = useValidation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { getTranslation } = useTranslation(translations);

  const { data: dataFetchCarrierContact, loading } =
    useSelector<RootStateOrAny>(
      (state) => state.fetchCarrierAreaContact
    ) as FetchCarrieAreaContactState;

  const { data: dataListCarriers, loading: loadingListCarriers } = useSelector<
    RootStateOrAny,
    ListCarriersState
  >((state) => state.listCarriers);

  const { loading: loadingDeleteCarrierAreaContact } =
    useSelector<RootStateOrAny>(
      (state) => state.deleteCarrierAreaContact
    ) as DeleteCarrierAreaContactState;

  const { loading: loadingUpdateCarrierAreaContact } =
    useSelector<RootStateOrAny>(
      (state) => state.updateCarrierAreaContact
    ) as UpdateCarrierAreaContactState;

  const onSuccess = useCallback(() => {
    history.push('/settings/carriers-contacts');
  }, [history]);

  const handleSubmit = useCallback<SubmitHandler>(
    async (data) => {
      try {
        formRef.current?.setErrors({});
        const schema = Yup.object().shape({
          name: Yup.string().required('Obrigatório'),
          phone_number: Yup.string().required('Obrigatório'),
          email: Yup.array()
            .transform(function (value, originalValue) {
              if (this.isType(value) && value !== null) {
                return value;
              }
              return originalValue ? originalValue.split(/[\s;]+/) : [];
            })
            .of(
              Yup.string().email(
                ({ value }) => `"${value}" não é um e-mail válido`
              )
            )
            .min(1, 'Obrigatório'),
          general_carrier_id: Yup.string().required('Obrigatório'),
          carrier_area_id: Yup.string().required('Obrigatório'),
          role: Yup.string().required('Obrigatório'),
        });
        await schema.validate(data, {
          abortEarly: false,
        });

        dispatch(UpdateCarrierAreaContactActions.request(id, data, onSuccess));
      } catch (error) {
        /* email validation hack */
        if (error instanceof Yup.ValidationError) {
          const indexMailError = error.inner.findIndex((err) =>
            err?.path?.includes('email')
          );
          if (indexMailError !== -1) {
            error.inner[indexMailError].path = 'email';
          }
        }
        handleFormErrors(error, formRef);
      }
    },
    [dispatch, handleFormErrors, id, onSuccess]
  );

  const putDataInFields = useCallback(
    (data) => {
      if (formRef.current && data.general_carrier_id) {
        const typeValue = dataListCarriers.find(
          (el: any) => el.value === data.general_carrier_id
        );

        formRef.current.setFieldValue('general_carrier_id', typeValue);
      }

      if (formRef.current && data.carrier_area_id) {
        const { id, name } = data.area;
        formRef.current.setFieldValue('carrier_area_id', {
          value: id,
          label: name,
        });
      }
    },
    [dataListCarriers]
  );

  useEffect(() => {
    dispatch(ListCarriersActions.request({ all: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFetchSuccess = useCallback(
    (data) => {
      if (formRef.current) {
        formRef.current.setData(data);
        putDataInFields(data);
      }
    },
    [putDataInFields]
  );

  const fetchData = useCallback(() => {
    if (id) {
      dispatch(FetchCarrierAreaContactActions.request(id, onFetchSuccess));
    }
  }, [dispatch, id, onFetchSuccess]);

  const onDeleteSuccess = useCallback(() => {
    history.goBack();
  }, [history]);

  const deleteData = useCallback(() => {
    dispatch(DeleteCarrierAreaContactActions.request(id, onDeleteSuccess));
  }, [dispatch, id, onDeleteSuccess]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <MainContainer>
      <Modal isOpen={modalOpen}>
        <Alert
          title={`Remover Contato`}
          text={`${getTranslation('removerContato')} ${dataFetchCarrierContact?.name} ${getTranslation('daTransportadora')} ${dataFetchCarrierContact?.carrier.trade_name}?`}
          close={() => setModalOpen(false)}
          action={deleteData}
          labelAction="Remover"
          isLoading={loadingDeleteCarrierAreaContact}
        />
      </Modal>
      <S.PageHeader>
        <h1>
          <S.IconSetting />
          {getTranslation('configuracoes')}{' '}
          <span>{getTranslation('editarTransportadora')}</span>
          {loading && <S.LoadingPage />}
        </h1>
        <S.HeaderButtons>
          <S.ButtonMini btStyle="dark" onClick={() => history.goBack()}>
            <S.IconArrowLeft />
            {getTranslation('voltar')}
          </S.ButtonMini>
        </S.HeaderButtons>
      </S.PageHeader>
      <S.PageContent>
        <Form ref={formRef} onSubmit={handleSubmit}>
          <S.BoxContainer>
            <S.FormRow>
              <Input name="name" label={getTranslation('nome')} />
              <InputMask
                name="phone_number"
                label={getTranslation('telefone')}
                mask="(99) 9999-99999"
              />
              <Input name="email" label={getTranslation('email')} />
            </S.FormRow>

            <S.FormRow>
              <Input
                name="role"
                label={getTranslation('funcao')}
                placeholder=""
              />
              <Select
                name="carrier_area_id"
                label={getTranslation('area')}
                options={[
                  {
                    value: 1,
                    label: 'Comercial',
                  },
                  {
                    value: 2,
                    label: 'Nosso Focal',
                  },
                  {
                    value: 3,
                    label: 'Faturamento/Contas a Receber',
                  },
                  {
                    value: 4,
                    label: 'Armazém',
                  },
                ]}
                placeholder={getTranslation('selecione')}
              />
              <Select
                name="general_carrier_id"
                label={getTranslation('transportadora')}
                isLoading={loadingListCarriers}
                options={dataListCarriers}
                placeholder={getTranslation('selecione')}
              />
            </S.FormRow>
          </S.BoxContainer>

          <S.FormFooter>
            <S.FormRow>
              <S.Button
                btStyle="cancel"
                type="button"
                onClick={() => history.goBack()}
              >
                {getTranslation('cancelar')}
              </S.Button>
              <S.Button
                btStyle="danger"
                type="button"
                onClick={() => setModalOpen(true)}
              >
                Remover
              </S.Button>
              <S.Button type="submit">
                {loadingUpdateCarrierAreaContact ? (
                  <S.Loading />
                ) : (
                  getTranslation('cadastrar')
                )}
              </S.Button>
            </S.FormRow>
          </S.FormFooter>
        </Form>
      </S.PageContent>
    </MainContainer>
  );
};
